/**
 * FinancialReportTab
 *
 * Displays settlement recovery data with date range filter.
 * Summary cards + sortable table + CSV export.
 */
import React, { useState, useCallback } from "react";
import { api } from "../../services/api.js";

interface FinancialRow {
  case_id: string;
  borrower_name?: string;
  borrower_id?: string;
  asset_category?: string;
  loan_amount?: number;
  loan_currency?: string;
  settlement_amount?: number;
  settlement_currency?: string;
  settled_at?: string;
  recovery_pct?: number;
}

interface Summary {
  total_cases: number;
  total_loan_value: number;
  total_settled: number;
  average_recovery_pct: number;
  highest_single_recovery: number;
}

const TH: React.CSSProperties = {
  padding: "10px 14px", textAlign: "left", fontWeight: 600,
  fontSize: 12, color: "var(--muted, #64748b)", textTransform: "uppercase",
  letterSpacing: "0.05em", borderBottom: "1px solid var(--border, #e2e8f0)",
  background: "var(--panel, #f8fafc)", whiteSpace: "nowrap",
};
const TD: React.CSSProperties = {
  padding: "10px 14px", fontSize: 14, color: "var(--ink, #0f172a)",
  borderBottom: "1px solid var(--border, #e2e8f0)", whiteSpace: "nowrap",
};

function fmtMoney(amount?: number, currency?: string): string {
  if (amount == null) return "—";
  return `${(amount / 1_000).toFixed(0)}K ${currency ?? "ETB"}`;
}
function fmtDate(iso?: string): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}

function StatCard({ label, value, sub }: { label: string; value: string; sub?: string }): React.ReactElement {
  return (
    <div style={{
      background: "var(--panel, #f8fafc)",
      border: "1px solid var(--border, #e2e8f0)",
      borderRadius: "var(--radius, 8px)",
      padding: "16px 20px",
      flex: 1,
    }}>
      <div style={{ fontSize: 12, color: "var(--muted, #64748b)", marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 700, color: "var(--ink, #0f172a)" }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: "var(--muted)", marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

export function FinancialReportTab(): React.ReactElement {
  const now = new Date();
  const [from, setFrom] = useState(`${now.getFullYear()}-01`);
  const [to, setTo] = useState(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`);
  const [rows, setRows] = useState<FinancialRow[]>([]);
  const [summary, setSummary] = useState<Summary | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);

  const fetch = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.reports.financial({ from, to });
      setRows(res.data.rows as unknown as FinancialRow[]);
      setSummary(res.data.summary as Summary);
      setLoaded(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load report");
    } finally {
      setLoading(false);
    }
  }, [from, to]);

  function exportCsv(): void {
    const headers = ["Case ID", "Borrower", "Asset", "Loan Amount", "Settlement Amount", "Recovery %", "Settled Date"];
    const lines = rows.map((r) =>
      [
        r.case_id,
        r.borrower_name ?? r.borrower_id ?? "—",
        r.asset_category ?? "—",
        r.loan_amount ?? "—",
        r.settlement_amount ?? "—",
        r.recovery_pct != null ? `${r.recovery_pct.toFixed(1)}%` : "—",
        fmtDate(r.settled_at),
      ].join(",")
    );
    const csv = [headers.join(","), ...lines].join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = `financial_report_${from}_${to}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div>
      {/* Filter bar */}
      <div style={{ display: "flex", gap: 12, alignItems: "flex-end", marginBottom: 24, flexWrap: "wrap" }}>
        <div>
          <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "var(--muted)", marginBottom: 4 }}>From (YYYY-MM)</label>
          <input
            type="month" value={from} onChange={(e) => setFrom(e.target.value)}
            style={{ padding: "7px 10px", border: "1px solid var(--border, #e2e8f0)", borderRadius: 6, fontSize: 14 }}
          />
        </div>
        <div>
          <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "var(--muted)", marginBottom: 4 }}>To (YYYY-MM)</label>
          <input
            type="month" value={to} onChange={(e) => setTo(e.target.value)}
            style={{ padding: "7px 10px", border: "1px solid var(--border, #e2e8f0)", borderRadius: 6, fontSize: 14 }}
          />
        </div>
        <button
          id="btn-run-financial-report"
          onClick={() => { void fetch(); }}
          disabled={loading}
          style={{
            padding: "8px 20px", background: "var(--brand, #6366f1)", color: "#fff",
            border: "none", borderRadius: 6, cursor: loading ? "not-allowed" : "pointer",
            fontWeight: 600, fontSize: 14, opacity: loading ? 0.7 : 1,
          }}
        >
          {loading ? "Loading…" : "Run Report"}
        </button>
        {loaded && rows.length > 0 && (
          <button
            id="btn-export-csv"
            onClick={exportCsv}
            style={{
              padding: "8px 16px", background: "transparent",
              border: "1px solid var(--border, #e2e8f0)", borderRadius: 6,
              cursor: "pointer", fontWeight: 500, fontSize: 14,
              color: "var(--ink, #0f172a)",
            }}
          >
            ⬇ Export CSV
          </button>
        )}
      </div>

      {error && (
        <div style={{ background: "#fef2f2", border: "1px solid #fca5a5", borderRadius: 8, padding: 14, color: "#991b1b", marginBottom: 20 }}>
          {error}
        </div>
      )}

      {/* Summary cards */}
      {summary && (
        <div style={{ display: "flex", gap: 12, marginBottom: 28, flexWrap: "wrap" }}>
          <StatCard label="Settlements in Period" value={String(summary.total_cases)} />
          <StatCard label="Total Loan Value" value={fmtMoney(summary.total_loan_value)} />
          <StatCard label="Total Recovered" value={fmtMoney(summary.total_settled)} />
          <StatCard label="Avg. Recovery %" value={`${summary.average_recovery_pct}%`} />
          <StatCard label="Highest Single Recovery" value={fmtMoney(summary.highest_single_recovery)} />
        </div>
      )}

      {/* Table */}
      {loaded && (
        <div style={{ overflowX: "auto", borderRadius: 8, border: "1px solid var(--border, #e2e8f0)" }}>
          {rows.length === 0 ? (
            <div style={{ padding: 40, textAlign: "center", color: "var(--muted)" }}>
              No settlements found for the selected period.
            </div>
          ) : (
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th style={TH}>Case ID</th>
                  <th style={TH}>Borrower</th>
                  <th style={TH}>Asset</th>
                  <th style={TH}>Loan Amount</th>
                  <th style={TH}>Settlement</th>
                  <th style={TH}>Recovery %</th>
                  <th style={TH}>Settled Date</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={r.case_id} style={{ background: i % 2 === 0 ? "#fff" : "var(--panel, #f8fafc)" }}>
                    <td style={{ ...TD, fontFamily: "monospace", fontSize: 12 }}>{r.case_id}</td>
                    <td style={TD}>{r.borrower_name ?? r.borrower_id ?? "—"}</td>
                    <td style={TD}>{r.asset_category ?? "—"}</td>
                    <td style={TD}>{fmtMoney(r.loan_amount, r.loan_currency)}</td>
                    <td style={TD}>{fmtMoney(r.settlement_amount, r.settlement_currency)}</td>
                    <td style={TD}>
                      {r.recovery_pct != null ? (
                        <span style={{
                          fontWeight: 600,
                          color: r.recovery_pct >= 80 ? "#16a34a" : r.recovery_pct >= 50 ? "#d97706" : "#dc2626",
                        }}>
                          {r.recovery_pct.toFixed(1)}%
                        </span>
                      ) : "—"}
                    </td>
                    <td style={TD}>{fmtDate(r.settled_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {!loaded && !loading && (
        <div style={{ padding: 60, textAlign: "center", color: "var(--muted)" }}>
          Select a date range and click <strong>Run Report</strong> to view financial settlements.
        </div>
      )}
    </div>
  );
}
